"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { SUPPORTED_CURRENCIES } from '../components/CurrencySelector';
import { useAuth } from './AuthContext';

interface FXRate {
  date: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
}

interface CurrencyContextType {
  // User preferences
  preferredCurrency: string;
  setPreferredCurrency: (currency: string) => void;
  
  // FX rates
  fxRates: Map<string, Map<string, number>>; // Nested Map: fromCurrency -> toCurrency -> rate
  latestRates: FXRate[];
  allHistoricRates: Map<string, Map<string, number>>;
  loading: boolean;
  error: string | null;
  
  // Currency conversion
  convertCurrency: (amount: number, fromCurrency: string, toCurrency: string, _date?: string) => Promise<number>;
  convertToGBP: (amount: number, fromCurrency: string, _date?: string) => Promise<number>;
  convertFromGBP: (amount: number, toCurrency: string, _date?: string) => Promise<number>;
  
  // Formatting
  formatCurrency: (amount: number, currency: string) => string;
  getCurrencySymbol: (currency: string) => string;
  
  // Refresh rates
  refreshRates: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [preferredCurrency, setPreferredCurrency] = useState<string>('GBP');
  const [fxRates, setFxRates] = useState<Map<string, Map<string, number>>>(new Map());
  const [latestRates, setLatestRates] = useState<FXRate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [allHistoricRates, setAllHistoricRates] = useState<Map<string, Map<string, number>>>(new Map());

  // Load user's preferred currency from localStorage
  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferredCurrency');
    if (savedCurrency && SUPPORTED_CURRENCIES.find(c => c.code === savedCurrency)) {
      setPreferredCurrency(savedCurrency);
    }
  }, []);

  // Fetch all historic FX rates for caching
  const fetchAllHistoricRates = useCallback(async () => {
    // Only fetch if user is logged in
    if (!user) return;
    
    try {
      const response = await fetch('/api/fx-rates/all-historic');
      if (!response.ok) {
        throw new Error(`Failed to fetch all historic FX rates: ${response.statusText}`);
      }
      
      const data = await response.json();
      const rates = new Map<string, Map<string, number>>();
      
      // Parse the nested rates object into our Map structure
      Object.entries(data.rates).forEach(([date, dateRates]: [string, unknown]) => {
        const dateMap = new Map<string, number>();
        Object.entries(dateRates as Record<string, number>).forEach(([pair, rate]: [string, number]) => {
          // Parse pair like "USD_to_GBP" into from/to currencies
          const [from, to] = pair.split('_to_');
          if (from && to) {
            dateMap.set(`${from}_${to}`, rate);
          }
        });
        rates.set(date, dateMap);
      });
      
      setAllHistoricRates(rates);
      console.log(`[CurrencyContext] Loaded ${data.count} historic FX rate dates into cache`);
    } catch (err) {
      console.error('Error loading all historic FX rates:', err);
    }
  }, [user]);

  const refreshRates = useCallback(async () => {
    // Only fetch if user is logged in
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/fx-rates/latest');
      if (!response.ok) {
        throw new Error(`Failed to fetch FX rates: ${response.statusText}`);
      }
      
      const data = await response.json();
      const rates = new Map<string, Map<string, number>>();
      
      // Parse the rates into a nested map structure
      const ratePairs = [
        { from: 'GBP', to: 'USD', rate: data.rates.GBP_to_USD },
        { from: 'GBP', to: 'EUR', rate: data.rates.GBP_to_EUR },
        { from: 'USD', to: 'GBP', rate: data.rates.USD_to_GBP },
        { from: 'EUR', to: 'GBP', rate: data.rates.EUR_to_GBP },
        { from: 'USD', to: 'EUR', rate: data.rates.USD_to_EUR },
        { from: 'EUR', to: 'USD', rate: data.rates.EUR_to_USD }
      ];
      
      ratePairs.forEach(({ from, to, rate }) => {
        if (!rates.has(from)) {
          rates.set(from, new Map());
        }
        rates.get(from)!.set(to, rate);
      });
      
      setFxRates(rates);
      
      // Create latest rates array for display
      const latestRatesArray: FXRate[] = ratePairs.map(({ from, to, rate }) => ({
        date: data.date,
        fromCurrency: from,
        toCurrency: to,
        rate
      }));
      
      setLatestRates(latestRatesArray);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load FX rates');
      console.error('Error loading FX rates:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initialize FX rates only when user is logged in
  useEffect(() => {
    if (user) {
      refreshRates();
      fetchAllHistoricRates(); // Fetch all historic rates for caching
    }
  }, [user, refreshRates, fetchAllHistoricRates]);

  const findClosestFxRate = useCallback(async (targetDate: string, fromCurrency: string, toCurrency: string): Promise<number | null> => {
    const rateKey = `${fromCurrency.toUpperCase()}_${toCurrency.toUpperCase()}`; // Changed from _to_ to just _
    
    // Helper function to parse date and get first/last day of month
    const getMonthBounds = (dateStr: string) => {
      const date = new Date(dateStr);
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth(); // 0-indexed month
      const firstDay = new Date(Date.UTC(year, month, 1));
      const lastDay = new Date(Date.UTC(year, month + 1, 0)); // Last day of current month
      return {
        first: firstDay.toISOString().split('T')[0],
        last: lastDay.toISOString().split('T')[0]
      };
    };
    
    // 1. Try exact date match first
    const exactDateRates = allHistoricRates.get(targetDate);
    if (exactDateRates) {
      const rate = exactDateRates.get(rateKey);
      if (rate) {
        return rate;
      }
    }
    
    // 2. Try first day of the month
    const { first: firstDay } = getMonthBounds(targetDate);
    const firstDayRates = allHistoricRates.get(firstDay);
    if (firstDayRates) {
      const rate = firstDayRates.get(rateKey);
      if (rate) {
        return rate;
      }
    }
    
    // 3. Try last day of the month
    const { last: lastDay } = getMonthBounds(targetDate);
    const lastDayRates = allHistoricRates.get(lastDay);
    if (lastDayRates) {
      const rate = lastDayRates.get(rateKey);
      if (rate) {
        return rate;
      }
    }
    
    // 4. Find the most recent rate before the target date
    const sortedDates = Array.from(allHistoricRates.keys())
      .filter(date => date < targetDate)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime()); // Most recent first
    
    for (const date of sortedDates) {
      const dateRates = allHistoricRates.get(date);
      if (dateRates) {
        const rate = dateRates.get(rateKey);
        if (rate) {
          return rate;
        }
      }
    }
    
    console.log(`No rate found for ${rateKey}`);
    return null; // No rate found
  }, [allHistoricRates]);

  const convertCurrency = useCallback(async (amount: number, fromCurrency: string, toCurrency: string, date?: string): Promise<number> => {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // If date is provided, try to get date-specific rates with fallback strategy
    if (date) {
      const rateKey = `${fromCurrency.toUpperCase()}_${toCurrency.toUpperCase()}`; // Changed from _to_ to just _
      const rate = await findClosestFxRate(date, fromCurrency, toCurrency);
      if (rate) {
        return amount * rate;
      }
      console.log("failed to get closed fx", date, fromCurrency, toCurrency)
      console.log("Available dates:", Array.from(allHistoricRates.keys()).slice(0, 10))
      console.log("Rate key:", rateKey)
      
      // Debug: Check what's in a sample date
      const sampleDate = '2024-03-01';
      const sampleRates = allHistoricRates.get(sampleDate);
      if (sampleRates) {
        console.log(`Rates for ${sampleDate}:`, Array.from(sampleRates.keys()));
      }
     
    }

    // Fallback to cached latest rates
    const fromRates = fxRates.get(fromCurrency.toUpperCase());
    if (!fromRates || fromRates.size === 0) {
      console.warn(`No FX rates available for ${fromCurrency}, returning unconverted amount`);
      return amount;
    }

    const rate = fromRates.get(toCurrency.toUpperCase());
    if (!rate) {
      console.warn(`No FX rate available for ${fromCurrency} to ${toCurrency}, returning unconverted amount`);
      return amount;
    }

    return amount * rate;
  }, [fxRates, allHistoricRates, findClosestFxRate]);

  const convertToGBP = useCallback(async (amount: number, fromCurrency: string, date?: string): Promise<number> => {
    return convertCurrency(amount, fromCurrency, 'GBP', date);
  }, [convertCurrency]);

  const convertFromGBP = useCallback(async (amount: number, toCurrency: string, date?: string): Promise<number> => {
    return convertCurrency(amount, 'GBP', toCurrency, date);
  }, [convertCurrency]);

  const formatCurrency = (amount: number, currency: string): string => {
    const currencyData = SUPPORTED_CURRENCIES.find(c => c.code === currency);
    if (!currencyData) {
      return amount.toFixed(2);
    }

    // Use Intl.NumberFormat for proper currency formatting
    try {
      const locale = getLocaleForCurrency(currency);
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(amount);
    } catch (_error) {
      // Fallback to simple formatting with thousands separators
      const formattedAmount = new Intl.NumberFormat('en-GB').format(Math.round(amount));
      return `${currencyData.symbol}${formattedAmount}`;
    }
  };

  const getCurrencySymbol = (currency: string): string => {
    const currencyData = SUPPORTED_CURRENCIES.find(c => c.code === currency);
    return currencyData?.symbol || currency;
  };

  const getLocaleForCurrency = (currency: string): string => {
    switch (currency) {
      case 'GBP':
        return 'en-GB';
      case 'USD':
        return 'en-US';
      case 'EUR':
        return 'de-DE'; // Using German locale for Euro formatting
      default:
        return 'en-US';
    }
  };

  const value: CurrencyContextType = {
    preferredCurrency,
    setPreferredCurrency,
    fxRates,
    latestRates,
    allHistoricRates,
    loading,
    error,
    convertCurrency,
    convertToGBP,
    convertFromGBP,
    formatCurrency,
    getCurrencySymbol,
    refreshRates
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
